// NEW_FILE_JUSTIFIED: New route page for template detail — no existing TemplateDetailPage
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: convex _generated
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: convex _generated
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { TemplateAssignmentDialog } from '@/components/control/TemplateAssignmentDialog';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';

export function TemplateDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const templateId = id as Id<'policyTemplates'>; // AS_JUSTIFIED: one-time Id cast from route params
  const [assignOpen, setAssignOpen] = useState(false);

  const template = useQuery(api.superadmin.queries.getTemplate, { templateId });
  const assignments = useQuery(api.superadmin.queries.getTemplateAssignments, { templateId });
  const removeAssignment = useMutation(api.superadmin.mutations.removeTemplateFromOrg);

  const isLoading = template === undefined;

  if (!isLoading && !template) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Template not found</p>
        <Button variant="link" onClick={() => navigate('/control/templates')}>
          Back to templates
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/control/templates')}>
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <h1 className="text-2xl font-semibold text-foreground">
          {template?.name ?? <Skeleton className="h-7 w-48 inline-block" />}
        </h1>
        {template && (
          <Badge variant={template.isActive ? 'default' : 'secondary'}>
            {template.isActive ? 'Active' : 'Inactive'}
          </Badge>
        )}
      </div>

      {template && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Template Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div>
                <span className="font-medium">Policy Type:</span> {template.policyTypeName}
              </div>
              <div>
                <span className="font-medium">Provider:</span> {template.providerName}
              </div>
              {template.description && (
                <div>
                  <span className="font-medium">Description:</span> {template.description}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                Field Definitions ({template.fields.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {template.fields.length === 0 ? (
                <p className="text-sm text-muted-foreground">No fields defined</p>
              ) : (
                <div className="space-y-2">
                  {template.fields.map((field, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-3 py-2 px-3 border border-border rounded-lg"
                    >
                      <span className="font-medium text-sm flex-1">{field.label}</span>
                      <Badge variant="outline" className="text-xs">
                        {field.fieldType}
                      </Badge>
                      {field.isRequired && (
                        <Badge variant="destructive" className="text-xs">
                          Required
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">
                Assigned Organisations ({assignments?.length ?? 0})
              </CardTitle>
              <Button size="sm" onClick={() => setAssignOpen(true)}>
                <Plus className="w-3.5 h-3.5 mr-1" />
                Assign
              </Button>
            </CardHeader>
            <CardContent>
              {(assignments ?? []).length === 0 ? (
                <p className="text-sm text-muted-foreground">Not assigned to any organisation</p>
              ) : (
                <div className="space-y-2">
                  {(assignments ?? []).map(assignment => (
                    <div
                      key={assignment._id}
                      className="flex items-center justify-between py-2 px-3 border border-border rounded-lg"
                    >
                      <span className="text-sm font-medium">{assignment.orgName}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          {new Date(assignment.assignedAt).toLocaleDateString('en-ZA')}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            try {
                              await removeAssignment({ assignmentId: assignment._id });
                              toast.success('Assignment removed');
                            } catch (e: unknown) {
                              const msg = e instanceof Error ? e.message : 'Failed';
                              toast.error(msg);
                            }
                          }}
                        >
                          <Trash2 className="w-3.5 h-3.5 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <TemplateAssignmentDialog
            open={assignOpen}
            onOpenChange={setAssignOpen}
            templateId={templateId}
            templateName={template.name}
          />
        </>
      )}
    </div>
  );
}
